import React, { useState, useMemo, useCallback, Suspense, useRef } from "react";
import clsx from "clsx";
import { FiFilter } from "react-icons/fi"; // Adicione este import no topo

// 2. IMPORTS DE COMPONENTES INTERNOS
import { Form } from "../Form/Form";
import Modal from "../Modal/Modal";
import InputField from "../InputField/InputField";
import FilterChips from "./FilterChips";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import FabMenu from "../FabMenu/FabMenu";
import Table from "./Table";
import SelectField from "../SelectField/SelectField";
import SkeletonTable from "./SkeletonTable";

// 3. IMPORTS DE LÓGICA/UTILS
import { formatValue, getCellAlignment, getMathStats } from "./ListUtils";
import { useDebouncedValue } from "./useDebouncedValue";
import {
  applyLocalFilters,
  applyFilterToRow,
  handleMultiFilter,
  supportsRemoteFiltering,
} from "./tableLogic";
import { generateXLSX } from "./GenerateXLSX";
import { useDataPipeline } from "./useDataPipeline";

// 4. TIPOS
import type { FormConfig } from "../Form/Form";
import type { Filter } from "./tableLogic";
import type { ColumnConfig } from "./columnsConfig";

// 5. LAZY IMPORTS DE MODAIS
const LazyCalcColumnModal = React.lazy(() => import("./CalcColumnModal"));
const LazyExportFilenameModal = React.lazy(
  () => import("./ExportFilenameModal")
);
const LazyMathMenuModal = React.lazy(() => import("./MathMenuModal"));

// 6. CONSTANTES GLOBAIS
const PAGE_SIZE = 24;

export const OPERATIONS = [
  { value: "==", label: "Igual" },
  { value: "!=", label: "Diferente" },
  { value: ">", label: "Maior que" },
  { value: "<", label: "Menor que" },
  { value: "contains", label: "Contém" },
];

export const LOGIC_OPERATORS = [
  { value: "AND", label: "E" },
  { value: "OR", label: "Ou" },
];

// 7. TIPOS DE AÇÃO DO FABMENU
type FabMenuAction = {
  key: string;
  label: string;
  icon?: React.ReactElement;
  onClick: () => Promise<void> | void;
  loading?: boolean;
  tooltip?: string;
  color?: string;
  disabled?: boolean;
  feedback?: string | null;
};

// 8. PROPS DO COMPONENTE PRINCIPAL
type DynamicTableProps<T extends Record<string, unknown>> = {
  data: T[];
  onDataChange: (data: T[]) => void;
  title: string;
  columnsConfig: ColumnConfig[];
  theme?: "light" | "dark";
  primaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  highContrast?: boolean;
  loading?: boolean;
  collectionName?: string;
  searchableFields?: string[];
  serverSide?: boolean;
  fetchPage?: (pageIndex: number, pageSize: number) => Promise<T[]>;
  fabMenuActions?: FabMenuAction[];
  autoRefresh?: boolean;
  refreshInterval?: number;
};

// (FabMenuActionButton removed because it was unused)

const DynamicTable = <T extends Record<string, unknown>>({
  data,
  onDataChange,
  title,
  columnsConfig,
  theme = "light",
  primaryColor,
  accentColor,
  fontFamily,
  loading = false,
  collectionName,
  searchableFields, // NOVO
  serverSide = false,
  fetchPage,
  fabMenuActions: fabMenuActionsProp,
  autoRefresh = false,
  refreshInterval = 30000,
}: DynamicTableProps<T>) => {
  // Export done state must be inside the component
  const [exportDone, setExportDone] = useState<boolean>(false);

  // Detecta tema do SO se theme="auto"
  const resolvedTheme = theme === "dark" ? "dark" : "light";

  React.useEffect(() => {
    const root = document.documentElement;
    if (primaryColor) root.style.setProperty("--primary-color", primaryColor);
    if (accentColor) root.style.setProperty("--accent-color", accentColor);
    if (fontFamily) root.style.setProperty("--font-family", fontFamily);
    if (resolvedTheme === "dark") root.setAttribute("data-theme", "dark");
    else root.setAttribute("data-theme", "light");
    return () => {
      root.removeAttribute("data-theme");
    };
  }, [primaryColor, accentColor, fontFamily, resolvedTheme]);

  // Novo: armazena os dados originais para restaurar e filtrar localmente
  const [originalData, setOriginalData] = useState<T[]>(data);

  // Estados que precisam ser definidos antes do useEffect que os utiliza
  const [currentPage, setCurrentPage] = useState(1);
  // Use PAGE_SIZE constante
  const pageSize = PAGE_SIZE;

  // Atualiza originalData se data mudar externamente
  React.useEffect(() => {
    setOriginalData(data);
  }, [data]);

  React.useEffect(() => {
    if (serverSide && fetchPage) {
      fetchPage(currentPage, pageSize).then(setOriginalData);
    }
  }, [serverSide, fetchPage, currentPage, pageSize]);

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [globalSearch, setGlobalSearch] = useState(""); // estado para busca global
  const debouncedSearch = useDebouncedValue(globalSearch, 300); // use o hook
  const [globalFilterCol, setGlobalFilterCol] = useState<string>(""); // <-- Add this line
  const [mathMenuCol, setMathMenuCol] = useState<string | null>(null);
  const [mathResult, setMathResult] = useState<string | null>(null);
  const [customColumns, setCustomColumns] = useState<
    { name: string; expr: string }[]
  >([]);
  const [calcModalOpen, setCalcModalOpen] = useState(false);
  const [calcColName, setCalcColName] = useState("");
  const [calcColExpr, setCalcColExpr] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [showColDropdown, setShowColDropdown] = useState(false);
  const [groupBy, setGroupBy] = useState<string | null>(null);
  const [pendingExport, setPendingExport] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // NOVO: ref para AbortController da exportação
  const exportAbortController = useRef<AbortController | null>(null);

  // workingData precisa ser declarado antes de ser usado
  const workingData = React.useMemo(() => originalData, [originalData]);

  // Memoização das colunas
  const columns = React.useMemo(() => Object.keys(data[0] ?? {}), [data]);

  // Gere as chaves das colunas a partir do columnsConfig recebido
  const columnKeys = React.useMemo(
    () => columnsConfig.map((col) => col.key),
    [columnsConfig]
  );

  // Controle de colunas visíveis baseado nas chaves do schema
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("visibleColumns");
      return saved ? JSON.parse(saved) : columnKeys;
    }
    return columnKeys;
  });

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
    }
  }, [visibleColumns]);

  // Memoização dos dados filtrados pela busca global
  const filteredData = React.useMemo(() => {
    if (!debouncedSearch) return workingData;
    const search = debouncedSearch.toLowerCase();
    return workingData.filter((row) => {
      const fields =
        searchableFields && searchableFields.length > 0
          ? searchableFields
          : Object.keys(row);
      return fields.some((field) => {
        const v = row[field];
        return (
          v !== undefined &&
          v !== null &&
          String(v).toLowerCase().includes(search)
        );
      });
    });
  }, [workingData, debouncedSearch, searchableFields]);

  // Memoização dos dados ordenados
  const sortedData = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    if (!sortCol) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortCol];
      const bVal = b[sortCol];
      if (aVal === bVal) return 0;
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredData, sortCol, sortDir]);
  const totalPages =
    sortedData && sortedData.length > 0
      ? Math.ceil(sortedData.length / pageSize)
      : 1;

  // Calcule as contagens de valores por coluna para o TableFooter

  // Decide se usa filtro remoto ou local
  const handleFilter = useCallback(
    async (filters: Filter[]) => {
      setFilters(filters);
      if (collectionName && supportsRemoteFiltering(filters)) {
        await handleMultiFilter(
          filters,
          collectionName,
          setOriginalData,
          onDataChange
        );
      } else {
        // Filtro local
        const filtered = applyLocalFilters(originalData, filters);
        onDataChange(filtered as T[]);
      }
    },
    [collectionName, onDataChange, originalData]
  );

  // Handler do Form
  const handleFormAction = useCallback(
    async (action: string, formData: Record<string, unknown>) => {
      if (action === "filter" && globalFilterCol) {
        const f1: Filter = {
          col: globalFilterCol,
          op: formData.op1 as string,
          value: formData.value1 as string,
        };
        const f2: Filter = {
          col: globalFilterCol,
          op: formData.op2 as string,
          value: formData.value2 as string,
        };
        const logic = formData.logic as string;

        let newFilters: Filter[] = [];
        if (logic === "AND") {
          newFilters = [...filters.filter((f) => f.col !== globalFilterCol), f1, f2];
        } else {
          newFilters = [
            ...filters.filter((f) => f.col !== globalFilterCol),
            { ...f1, logic: "OR", orWith: f2 },
          ];
        }
        await handleFilter(newFilters);
        setFilterModalOpen(false);
        setGlobalFilterCol("");
      }
      if (action === "cancel") {
        setFilterModalOpen(false);
        setGlobalFilterCol("");
      }
    },
    [globalFilterCol, filters, handleFilter]
  );

  const removeFilter = useCallback(
    (id: string): void => {
      const newFilters = filters.filter((f) => f.col !== id);
      setFilters(newFilters);
      // Aplica os filtros restantes localmente usando originalData
      const filtered = applyLocalFilters(originalData, newFilters);
      onDataChange(filtered as T[]);
    },
    [filters, originalData, onDataChange]
  );

  const clearAllFilters = useCallback((): void => {
    setFilters([]);
    onDataChange(originalData); // Restaura todos os dados originais
  }, [originalData, onDataChange]);

  // Configuração dinâmica do Formulário de filtro
  const getFormConfig = (): FormConfig => ({
    title: `Filtrar`,
    fields: [
      [
        {
          id: "col",
          type: "select",
          label: "Coluna",
          options: visibleColumns.map((col) => ({
            value: col,
            label: columnsConfig.find((c) => c.key === col)?.label || col,
          })),
          required: true,
        },
      ],
      [
        {
          id: "op1",
          type: "select",
          label: "Operação 1",
          options: OPERATIONS,
          required: true,
        },
        {
          id: "value1",
          type: "input",
          inputType: "text",
          label: "Valor 1",
          required: true,
        },
      ],
      [
        {
          id: "logic",
          type: "select",
          label: "Operador",
          options: LOGIC_OPERATORS,
          required: true,
        },
      ],
      [
        {
          id: "op2",
          type: "select",
          label: "Operação 2",
          options: OPERATIONS,
          required: true,
        },
        {
          id: "value2",
          type: "input",
          inputType: "text",
          label: "Valor 2",
          required: true,
        },
      ],
    ],
    buttons: {
      create: [
        { title: "Filtrar", action: "filter" },
        { title: "Cancelar", action: "cancel" },
      ],
    },
  });

  // Sempre chame hooks antes de qualquer return condicional!
  // (Removido bloco duplicado de useDataPipeline)

  // Sempre chame hooks antes de qualquer return condicional!
  // (Removido bloco duplicado de useDataPipeline)

  // Sempre chame hooks antes de qualquer return condicional!
  const {
    data: dataWithCalc,
    // ...outros estágios se quiser
  } = useDataPipeline({
    data: originalData,
    search: debouncedSearch,
    searchableFields,
    filters,
    sortCol,
    sortDir,
    page: currentPage,
    pageSize,
    customColumns,
  });

  // Adicione estado para locale de exportação
  const [exportLocale] = useState<string>(navigator.language || "pt-BR");

  // 1. Adicione estado para progresso de exportação
  const [exportProgress, setExportProgress] = useState<number>(0);

  // Função de exportação usando generateXLSX com todos os recursos
  const handleExport = useCallback(
    async (filenamePrefix: string) => {
      setPendingExport(true);
      setExportProgress(0);
      setExportDone(false);
      setExportError(null);

      const controller = new AbortController();
      exportAbortController.current = controller;

      try {
        const header = [...visibleColumns, ...customColumns.map((c) => c.name)];
        const rows = dataWithCalc.map(
          (row) =>
            header.map((col) => {
              const value = row[col];
              if (
                typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean" ||
                value instanceof Date
              ) {
                return value;
              }
              return value != null ? String(value) : "";
            }) as (string | number | boolean | Date)[]
        );

        const chunkFilename = (idx: number, total: number) =>
          `${filenamePrefix}_parte_${idx + 1}_de_${total}.xlsx`;

        await generateXLSX(header, rows, {
          filenamePrefix,
          maxRows: 50000,
          chunkFilename,
          locale: exportLocale,
          signal: controller.signal,
          onProgress: (progress: { current: number; total: number }) => {
            const percent =
              progress.total > 0
                ? (progress.current / progress.total) * 100
                : 0;
            setExportProgress(percent);
          },
          onDone: () => setExportDone(true),
        }).then(({ blob, filename }) => {
          // Download seguro
          const url = URL.createObjectURL(blob);
          function sanitizeFilename(filename: string): string {
            return filename.replace(/[^a-zA-Z0-9_\-.]/g, "_");
          }
          const a = document.createElement("a");
          a.href = url;
          a.download = sanitizeFilename(filename);
          a.click();
          URL.revokeObjectURL(url);
        });
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === "AbortError") {
          setExportError("Exportação cancelada pelo usuário.");
        } else if (e && typeof e === "object" && "message" in e) {
          setExportError(
            (e as { message?: string }).message || "Erro ao exportar."
          );
        } else {
          setExportError("Erro ao exportar.");
        }
      } finally {
        setPendingExport(false);
        exportAbortController.current = null;
        setExportProgress(0); // Reseta progresso ao finalizar
      }
    },
    [visibleColumns, customColumns, dataWithCalc, exportLocale]
  );
  useHotkeys("ctrl+f", (e) => {
    e.preventDefault();
    (
      document.querySelector('input[type="search"]') as HTMLInputElement | null
    )?.focus();
  });
  useHotkeys("ctrl+shift+c", () => setCalcModalOpen(true));
  useHotkeys("ctrl+shift+e", () => setExportModalOpen(true));
  useHotkeys("ctrl+shift+r", () => clearAllFilters());
  useHotkeys("esc", () => {
    setFilterModalOpen(false);
    setCalcModalOpen(false);
    setExportModalOpen(false);
  });
  useHotkeys("pagedown", () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1))
  );
  useHotkeys("pageup", () => setCurrentPage((p) => Math.max(1, p - 1)));
  useHotkeys("home", () => setCurrentPage(1));
  useHotkeys("end", () => setCurrentPage(totalPages));

  // Função utilitária para calcular largura ideal em 'ch'
  const getMaxColWidth = useCallback(
    (
      col: string,
      data: T[],
      header: string,
      min = 15, // <-- aumente para 18 ou 20
      max = 32
    ): string => {
      // Some +8 ao header para garantir espaço dos botões
      const headerLen = header.length + 8;
      const maxCell = Math.max(
        ...data.map((row) =>
          row[col] !== undefined && row[col] !== null
            ? String(row[col]).length
            : 0
        ),
        headerLen
      );
      return `${Math.max(min, Math.min(max, maxCell + 2))}ch`;
    },
    []
  );

  const clampWidth = (w: string) => {
    // só clampa se for px
    if (w.endsWith("px")) {
      const px = parseInt(w, 10);
      return `${Math.max(120, Math.min(240, px))}px`; // ajuste para máximo menor
    }
    // Se for em 'ch', converta para px e garanta mínimo
    if (w.endsWith("ch")) {
      const ch = parseInt(w, 10);
      const px = Math.max(120, Math.min(240, Math.round(ch * 8)));
      return `${px}px`;
    }
    return w;
  };
  // Calcule as larguras das colunas visíveis + customColumns
  const allCols = useMemo(
    () => [...visibleColumns, ...customColumns.map((c) => c.name)],
    [visibleColumns, customColumns]
  );
  const initialColumnWidths = useMemo(
    () =>
      allCols.map((col) =>
        clampWidth(
          getMaxColWidth(
            col,
            sortedData,
            col
              .replace(/_/g, " ")
              .replace(
                /\w\S*/g,
                (txt: string) =>
                  txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
              )
          )
        )
      ),
    [allCols, sortedData, getMaxColWidth]
  );

  // Estado controlado para as larguras das colunas
  const [columnWidths, setColumnWidths] =
    React.useState<string[]>(initialColumnWidths);

  React.useEffect(() => {
    setColumnWidths(initialColumnWidths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCols, sortedData.length]);

  // Handler para reordenação de colunas (drag and drop)
  function handleColumnOrderChange(newOrder: string[]) {
    setVisibleColumns(
      newOrder.filter(
        (col) =>
          columns.includes(col) || customColumns.some((c) => c.name === col)
      )
    );
    // Se a coluna de ordenação sumiu, limpa o sortCol
    if (sortCol && !newOrder.includes(sortCol)) {
      setSortCol(null);
    }
  }

  // Só depois dos hooks, faça os returns condicionais:
  // Memoize getCellAlignment e formatValue
  const memoGetCellAlignment = useCallback(getCellAlignment, []);
  const memoFormatValue = useCallback(formatValue, []);

  // Memoize isRowFiltered
  const memoIsRowFiltered = useCallback(
    (row: Record<string, unknown>) => {
      if (filters.length === 0) return false;
      return !filters.every((f) => applyFilterToRow(row, f));
    },
    [filters]
  );

  // Dentro do componente DynamicTable, antes do return:
  const fabMenuDefaultActions: FabMenuAction[] = React.useMemo(
    () => [
      {
        key: "export-xlsx",
        label: "Excel",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <rect width="20" height="20" rx="3" fill="#217346" />
            <text x="4" y="15" fontSize="11" fill="#fff" fontWeight="bold">
              X
            </text>
          </svg>
        ) as React.ReactElement,
        onClick: () => setExportModalOpen(true),
        loading: pendingExport,
        tooltip: "Excel",
        color: undefined,
        disabled: false,
        feedback: null,
      },
      {
        key: "add-calc-column",
        label: "Coluna calculada",
        icon: (
          <span style={{ fontWeight: "bold", fontSize: 18 }}>+</span>
        ) as React.ReactElement,
        onClick: () => setCalcModalOpen(true),
        tooltip: "Adicionar coluna calculada",
        color: undefined,
        disabled: false,
        feedback: null,
      },
      {
        key: "toggle-columns",
        label: "Colunas",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="16" height="3" rx="1" fill="#1976d2" />
            <rect x="2" y="9" width="16" height="3" rx="1" fill="#1976d2" />
            <rect x="2" y="14" width="16" height="3" rx="1" fill="#1976d2" />
          </svg>
        ) as React.ReactElement,
        onClick: () => setShowColDropdown((v) => !v),
        tooltip: "Mostrar/ocultar colunas",
        color: undefined,
        disabled: false,
        feedback: null,
      },
    ],
    [pendingExport]
  );

  // Junta as ações padrão com as customizadas (se houver)
  const fabMenuActions: FabMenuAction[] = React.useMemo(() => {
    let customActions: FabMenuAction[] = [];
    if (
      Array.isArray(fabMenuActionsProp) &&
      fabMenuActionsProp.length > 0 &&
      typeof fabMenuActionsProp[0] === "object" &&
      "key" in fabMenuActionsProp[0]
    ) {
      customActions = fabMenuActionsProp as FabMenuAction[];
    } else if (fabMenuActionsProp) {
      customActions = Array.isArray(fabMenuActionsProp)
        ? (fabMenuActionsProp as FabMenuAction[])
        : [fabMenuActionsProp as FabMenuAction];
    }
    return [...fabMenuDefaultActions, ...customActions];
  }, [fabMenuActionsProp, fabMenuDefaultActions]);

  // 1. Novo estado para seleção de linhas
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);

  // Estado para seleções matemáticas por coluna
  const [mathSelections, setMathSelections] = useState<Record<string, string>>(
    {}
  );

  // Efeito de auto-refresh/polling
  React.useEffect(() => {
    if (!autoRefresh) return;
    let timeout: NodeJS.Timeout;
    let cancelled = false;

    async function poll() {
      setRefreshing(true);
      try {
        if (serverSide && fetchPage) {
          const newData = await fetchPage(currentPage, pageSize);
          if (!cancelled) setOriginalData(newData);
        } else {
          // Se não for serverSide, apenas simula um "refresh" (pode customizar)
          if (!cancelled) setOriginalData([...data]);
        }
      } finally {
        setRefreshing(false);
        if (!cancelled) {
          timeout = setTimeout(poll, refreshInterval);
        }
      }
    }

    timeout = setTimeout(poll, refreshInterval);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [
    autoRefresh,
    refreshInterval,
    serverSide,
    fetchPage,
    currentPage,
    pageSize,
    data,
  ]);

  // Agrupamento dos dados se groupBy estiver definido
  const groupedData = useMemo(() => {
    if (!groupBy) return [{ group: null, rows: sortedData }];
    const groups: Record<string, T[]> = {};
    for (const row of sortedData) {
      const key = String(row[groupBy] ?? "Vazio");
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }
    return Object.entries(groups).map(([group, rows]) => ({ group, rows }));
  }, [sortedData, groupBy]);

  const handleGlobalSearchChange = useCallback((value: unknown): void => {
    setGlobalSearch(typeof value === "string" ? value : String(value ?? ""));
  }, []);

  const handleGroupByChange = useCallback((v: unknown) => {
    setGroupBy(Array.isArray(v) ? v[0] : v || null);
  }, []);

  // Validação da estrutura dos dados
  if (!Array.isArray(data)) {
    return (
      <div
        className="list-error glassCard"
        style={{ color: "red", padding: 32, textAlign: "center" }}
      >
        <strong>Erro:</strong> Os dados fornecidos não são um array.
        <br />
        Certifique-se de passar um array de objetos para a prop{" "}
        <code>data</code>.
      </div>
    );
  }

  if (data.length > 0 && (typeof data[0] !== "object" || data[0] === null)) {
    return (
      <div
        className="list-error glassCard"
        style={{ color: "red", padding: 32, textAlign: "center" }}
      >
        <strong>Erro:</strong> Cada item dos dados deve ser um objeto.
        <br />
        Verifique se todos os elementos do array <code>data</code> são objetos
        válidos.
      </div>
    );
  }

  if (!Array.isArray(columnsConfig) || columnsConfig.length === 0) {
    return (
      <div
        className="list-error glassCard"
        style={{ color: "red", padding: 32, textAlign: "center" }}
      >
        <strong>Erro:</strong> <code>columnsConfig</code> não foi definido
        corretamente.
        <br />
        Forneça um array de configurações de colunas válido.
      </div>
    );
  }

  if (loading || refreshing) {
    return (
      <div
        className="list-loading glassCard"
        style={{ textAlign: "center", padding: 32 }}
      >
        <SkeletonTable rows={8} cols={visibleColumns.length || 5} />
        {refreshing && (
          <div style={{ marginTop: 16, color: "#1976d2" }}>
            Atualizando dados...
          </div>
        )}
      </div>
    );
  }

  if (!sortedData || sortedData.length === 0) {
    return (
      <div className="list-empty glassCard">
        {debouncedSearch ? (
          <>Nenhum resultado encontrado para &quot;{debouncedSearch}&quot;.</>
        ) : (
          <>Nenhum dado encontrado.</>
        )}
      </div>
    );
  }

  return (
    <div className="list-wrapper w-full h-full"> {/* <-- Wrapper adicionado */}
        {/* TÍTULO CENTRALIZADO NO TOPO */}
        <div className="w-full flex justify-center items-center mt-4 mb-2">
          <h2
            className={clsx(
              "text-center text-2xl font-semibold tracking-tight",
              theme === "dark" ? "text-gray-100" : "text-gray-900"
            )}
            style={{
              fontFamily: fontFamily
                ? `var(--font-family), sans-serif`
                : undefined,
            }}
          >
            {title}
            {autoRefresh && (
              <span
                className={clsx(
                  "inline-flex items-center gap-1 text-sm ml-2 align-middle",
                  refreshing ? "text-primary" : "text-gray-400"
                )}
                title={`Auto-refresh a cada ${refreshInterval / 1000}s`}
              >
                <span
                  className={refreshing ? "spinner" : ""}
                  style={{ width: 16, height: 16, display: "inline-block" }}
                />
                Atualizando
              </span>
            )}
          </h2>
        </div>

        {/* LINHA DE BUSCA E AGRUPAMENTO CENTRALIZADA */}
        <div className="w-full flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-center mb-3">
          <div className="w-full sm:w-auto">
            <InputField
              type="search"
              aria-label="Buscar em todos os campos"
              value={globalSearch}
              onChange={handleGlobalSearchChange}
              placeholder="Buscar em todos os campos..."
              theme={theme}
              containerProps={{ "data-testid": "list-search-input" }}
              tabIndex={0}
              autoFocus
              className="w-full min-w-[180px] h-10"
              inputClassName="h-10"
            />
          </div>
          {/* Botão único de filtro */}
          <Button
            title="Filtrar"
            icon={<FiFilter />}
            theme={theme}
            size="small"
            onClick={() => setFilterModalOpen(true)}
            buttonClassName="ml-2"
            data-testid="global-filter-btn"
          />
          <div className="w-full sm:w-auto">
            <SelectField
              options={[
                { value: "", label: "Sem agrupamento" },
                ...visibleColumns.map((col) => ({
                  value: col,
                  label: `Agrupar por ${col}`,
                })),
              ]}
              value={groupBy ?? ""}
              onChange={handleGroupByChange}
              aria-label="Agrupar por coluna"
              placeholder="Agrupar por"
              className="w-full min-w-[180px] h-10"
              inputClassName="h-10"
              data-testid="groupby-select"
              theme={theme}
            />
          </div>
        </div>

        {/* Chips de colunas calculadas, filtros ativos - CENTRALIZADO */}
        <div className="w-full flex flex-col items-center mb-4">
          {/* Chips de colunas calculadas */}
          {customColumns.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-2 justify-center">
              {customColumns.map((col, idx) => (
                <span
                  key={col.name}
                  className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-sm font-spaceGrotesk"
                >
                  {col.name}
                  <Button
                    ariaLabel={`Remover coluna ${col.name}`}
                    onClick={() => {
                      setCustomColumns(
                        customColumns.filter((_, i) => i !== idx)
                      );
                    }}
                    theme={theme}
                    buttonProps={{
                      className:
                        "ml-2 bg-transparent border-none text-gray-400 hover:text-red-500 p-0 min-w-0 min-h-0 h-auto w-auto",
                      tabIndex: 0,
                      title: `Remover coluna ${col.name}`,
                      "aria-label": `Remover coluna ${col.name}`,
                    }}
                    size="tiny"
                    data-testid={`remove-calc-col-${col.name}`}
                  >
                    &#10005;
                  </Button>
                </span>
              ))}
            </div>
          )}

          {/* Filtros ativos */}
          <div className="w-full flex justify-center mb-2">
            <FilterChips
              filters={filters}
              onRemove={removeFilter}
              onClearAll={clearAllFilters}
            />
          </div>
        </div>

        {/* TABELA E RESTANTE */}
        <div
          className={clsx(
            "w-full min-h-full flex justify-center items-start bg-transparent relative",
            resolvedTheme === "dark" ? "bg-[#23242a]" : "bg-white"
          )}
          style={{
            fontFamily: fontFamily ? `var(--font-family), sans-serif` : undefined,
          }}
        >
          {/* Remova a div das chips duplicada daqui! */}
          <div className="w-full flex flex-col items-center justify-center">
            <div className="flex flex-col items-center">
              {/* Paginação (exemplo, ajuste conforme seu componente de paginação) */}
              <div className="flex items-center justify-center gap-5 mt-5">
                {/* ...seus controles de paginação aqui... */}
              </div>

              {/* Skeleton de carregamento */}
              {(loading || refreshing) && (
                <div className="w-full">
                  <SkeletonTable
                    rows={8}
                    cols={visibleColumns.length || 5}
                    className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded"
                  />
                  {refreshing && (
                    <div className="mt-4 text-primary">Atualizando dados...</div>
                  )}
                </div>
              )}

              {/* Centralize a Table visualmente */}
              <div className="flex justify-center w-full">
                <div className="relative w-full px-6 overflow-x-auto max-w-full">
                  <div className="inline-block">
                    <Table
                      columns={visibleColumns}
                      customColumns={customColumns}
                      sortedData={groupBy ? groupedData : sortedData}
                      groupBy={groupBy}
                      columnWidths={columnWidths}
                      setColumnWidths={setColumnWidths}
                      sortCol={sortCol}
                      sortDir={sortDir}
                      toggleSort={(col) => {
                        if (sortCol !== col) {
                          setSortCol(col);
                          setSortDir("asc");
                        } else if (sortDir === "asc") {
                          setSortDir("desc");
                        } else {
                          setSortCol(null);
                        }
                      }}
                      setMathMenuCol={setMathMenuCol}
                      setFilterCol={(col: string) => setGlobalFilterCol(col)}
                      setFilterModalOpen={setFilterModalOpen}
                      isColFiltered={(col) => filters.some(f => f.col === col)}
                      onColumnOrderChange={handleColumnOrderChange}
                      hoverCol={hoverCol}
                      setHoverCol={setHoverCol}
                      formatValue={memoFormatValue}
                      getCellAlignment={memoGetCellAlignment}
                      isRowFiltered={memoIsRowFiltered}
                      mathSelections={mathSelections}
                      setMathSelections={setMathSelections}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      pageSize={pageSize}
                      totalPages={totalPages}
                      theme={theme}
                      columnsConfig={columnsConfig} // <-- ADICIONE ESTA LINHA
                      onHideColumn={(colKey: string) => {
                        setVisibleColumns((cols) => cols.filter((c) => c !== colKey));
                      }}
                    />
                  </div>
                </div>
              </div>
              <Modal
                open={filterModalOpen}
                onClose={() => {
                  setFilterModalOpen(false);
                }}
                theme={theme}
                outlined
                shadow="md"
                aria-label="Modal de filtro global"
                role="dialog"
                tabIndex={-1}
                className="modal-filtro"
              >
                <div className="flex flex-col gap-4">
                  <Form
                    config={getFormConfig()}
                    initialValues={{ col: globalFilterCol }} // <-- Passe aqui!
                    collectionName={collectionName ?? ""}
                    mode="create"
                    onAction={handleFormAction}
                    theme={theme}
                    i18n={{
                      "toast.success": "Filtro aplicado!",
                      "toast.error": "Erro ao filtrar",
                      "toast.close": "Fechar",
                    }}
                    inputFieldProps={{
                      tooltip: "Preencha o valor para filtrar",
                      autoFocus: true,
                    }}
                    inputFieldI18nMessages={{
                      getDefaultErrorMessage: () => "Formato inválido",
                      getDefaultSuccessMessage: () => "Campo válido",
                    }}
                  />
                </div>
              </Modal>
              <Suspense fallback={null}>
                <LazyCalcColumnModal
                  open={calcModalOpen}
                  onClose={() => {
                    setCalcModalOpen(false);
                    setCalcColName("");
                    setCalcColExpr("");
                  }}
                  calcColName={calcColName}
                  setCalcColName={setCalcColName}
                  calcColExpr={calcColExpr}
                  setCalcColExpr={setCalcColExpr}
                  onAdd={() => {
                    if (calcColName && calcColExpr) {
                      setCustomColumns([
                        ...customColumns,
                        { name: calcColName, expr: calcColExpr },
                      ]);
                      setCalcModalOpen(false);
                      setCalcColName("");
                      setCalcColExpr("");
                    }
                  }}
                  theme={theme}
                />
                {/* Remova o Modal que envolve LazyExportFilenameModal */}
                {/* Antes:
                <Modal
                  open={exportModalOpen}
                  onClose={() => setExportModalOpen(false)}
                  theme={theme}
                  header="Exportar para Excel"
                  outlined
                  shadow="md"
                  aria-label="Modal de exportação"
                  role="dialog"
                  tabIndex={-1}
                  className="modal-export"
                >
                  {/* Barra de progresso da exportação */}
                  {pendingExport &&
                    exportProgress > 0 &&
                    exportProgress < 100 && (
                      <div className="w-full mb-3">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${exportProgress}%` }}
                          />
                        </div>
                        <div className="text-xs text-center mt-1 text-gray-600">
                          {exportProgress.toFixed(0)}%
                        </div>
                      </div>
                    )}
                  {/* Mensagem de sucesso ao exportar */}
                  {exportDone && (
                    <div
                      className={clsx(
                        "mb-3 px-3 py-2 rounded font-medium text-center border",
                        "bg-green-100 border-green-300 text-green-800"
                      )}
                      data-testid="export-success"
                    >
                      Exportação concluída com sucesso!
                    </div>
                  )}
                  {/* Exibe mensagem de erro se houver */}
                  {exportError && (
                    <div
                      className={clsx(
                        "mb-3 px-3 py-2 rounded font-medium text-center border",
                        "bg-red-100 border-red-300 text-red-800"
                      )}
                      data-testid="export-error"
                    >
                      {exportError}
                    </div>
                  )}
                  <LazyExportFilenameModal
                    open={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    onSave={async (options: { filename: string }) => {
                      await handleExport(options.filename);
                      // Não feche o modal automaticamente, permita o usuário ver o feedback
                      // setExportModalOpen(false);
                    }}
                    initialValues={{
                      filename: `dados_${new Date().toISOString().slice(0, 10)}`
                    }}
                    theme={theme}
                    aria-label="Modal de exportação"
                  />
                {/* Barra de progresso da exportação */}
                {pendingExport &&
                  exportProgress > 0 &&
                  exportProgress < 100 && (
                    <div className="w-full mb-3">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${exportProgress}%` }}
                        />
                      </div>
                      <div className="text-xs text-center mt-1 text-gray-600">
                        {exportProgress.toFixed(0)}%
                      </div>
                    </div>
                  )}
                {/* Mensagem de sucesso ao exportar */}
                {exportDone && (
                  <div
                    className={clsx(
                      "mb-3 px-3 py-2 rounded font-medium text-center border",
                      "bg-green-100 border-green-300 text-green-800"
                    )}
                    data-testid="export-success"
                  >
                    Exportação concluída com sucesso!
                  </div>
                )}
                {/* Exibe mensagem de erro se houver */}
                {exportError && (
                  <div
                    className={clsx(
                      "mb-3 px-3 py-2 rounded font-medium text-center border",
                      "bg-red-100 border-red-300 text-red-800"
                    )}
                    data-testid="export-error"
                  >
                    {exportError}
                  </div>
                )}
                <LazyExportFilenameModal
                  open={exportModalOpen}
                  onClose={() => setExportModalOpen(false)}
                  onSave={async (options: { filename: string }) => {
                    await handleExport(options.filename);
                    // Não feche o modal automaticamente, permita o usuário ver o feedback
                    // setExportModalOpen(false);
                  }}
                  initialValues={{
                    filename: `dados_${new Date().toISOString().slice(0, 10)}`
                  }}
                  theme={theme}
                  aria-label="Modal de exportação"
                />
                <Modal
                  open={!!mathMenuCol}
                  onClose={() => {
                    setMathMenuCol(null);
                    setMathResult(null);
                  }}
                  theme={theme}
                  header={
                    mathMenuCol ? `Estatísticas: ${mathMenuCol}` : undefined
                  }
                  outlined
                  shadow="md"
                  aria-label={`Modal de estatísticas para ${mathMenuCol ?? ""}`}
                  role="dialog"
                  tabIndex={-1}
                  className="modal-math"
                >
                  <LazyMathMenuModal
                    open={!!mathMenuCol}
                    column={mathMenuCol}
                    data={sortedData}
                    getMathStats={getMathStats}
                    result={mathResult}
                    setResult={setMathResult}
                    onClose={() => {
                      setMathMenuCol(null);
                      setMathResult(null);
                    }}
                    theme={theme}
                  />
                </Modal>
              </Suspense>
            </div>
          </div>
          {/* FAB MENU */}
          <div className="absolute bottom-8 right-8 z-[1000]">
            <FabMenu
              label="Ações rápidas"
              theme={theme}
              direction="up"
              actions={fabMenuActions}
            />
            {showColDropdown && (
              <div className="fixed bottom-24 right-8 bg-white border border-gray-300 rounded-md p-3 z-[1001] shadow-lg">
                {columnsConfig.map((col) => (
                  <label
                    key={col.key}
                    className={clsx(
                      "block mb-1",
                      col.essential
                        ? "font-bold opacity-100"
                        : "font-normal opacity-70"
                    )}
                    aria-label={`Coluna ${col.label}`}
                  >
                    <Checkbox
                      checked={visibleColumns.includes(col.key)}
                      onChange={() => {
                        setVisibleColumns((cols) =>
                          cols.includes(col.key)
                            ? cols.filter((c) => c !== col.key)
                            : [...cols, col.key]
                        );
                      }}
                      label={col.label}
                      theme={theme}
                      tabIndex={0}
                      containerProps={{ className: "inline-flex items-center" }}
                      data-testid={`col-checkbox-${col.key}`}
                      disabled={col.essential}
                      aria-disabled={col.essential ? "true" : undefined}
                      aria-label={`Alternar visibilidade da coluna ${col.label}`}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
          {/* Removido botão vazio */}
          {/* 5. Exemplo de menu de ações rápidas para linhas selecionadas */}
          {selectedRows.size > 0 && (
            <div className="fixed bottom-24 right-8 z-[1001] bg-white rounded-lg shadow-lg p-3 flex items-center gap-3">
              <span>{selectedRows.size} selecionado(s)</span>
              <Button
                theme={theme}
                onClick={() => {
                  // Exemplo: deletar selecionados
                  onDataChange(
                    data.filter((row) => !selectedRows.has(String(row.id)))
                  );
                  setSelectedRows(new Set());
                }}
                buttonProps={{
                  className:
                    "rounded-md px-4 py-2 font-medium bg-primary text-white hover:bg-blue-800 transition ml-3",
                }}
              >
                Deletar selecionados
              </Button>
              {/* Adicione outros botões de batch aqui */}
            </div>
          )}
        </div>
    </div>
  );
};

export default DynamicTable;
export type { DynamicTableProps as ListProps };
function useHotkeys(keys: string, callback: (e: KeyboardEvent) => void) {
  React.useEffect(() => {
    function handler(e: KeyboardEvent) {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey;
      const shift = e.shiftKey;
      const alt = e.altKey;

      // Parse keys string, e.g. "ctrl+shift+c"
      const parts = keys
        .toLowerCase()
        .split("+")
        .map((s) => s.trim());
      const requiredCtrl = parts.includes("ctrl");
      const requiredShift = parts.includes("shift");
      const requiredAlt = parts.includes("alt");
      const mainKey = parts.find(
        (p) => p !== "ctrl" && p !== "shift" && p !== "alt"
      );

      if (
        ctrl === requiredCtrl &&
        shift === requiredShift &&
        alt === requiredAlt &&
        (mainKey ? key === mainKey : true)
      ) {
        callback(e);
      }
    }

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [keys, callback]);
}
